import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getCardDetails } from "sr/utils/api/strip/getCardDetails";
import {
  downloadCompanyBackgroundCheckReport,
  downloadCompanyBackgroundCheckReportDocument,
  getCheckrPackages,
  getCompanyLatestBackgroundCheck,
  startCompanyContractorBackgroundCheck,
} from "sr/utils/api/checkr/companyContractorCheckr";
import {
  CheckrPackageSelectionDialog,
  type CheckrPackage,
} from "app/pages/component/CheckrPackageSelectionDialog";

type PaymentCard = {
  id: string;
  card?: { last4?: string; brand?: string };
  isDefault?: boolean;
};

type BackgroundCheckDetails = {
  status?: string;
  result?: string;
  package?: string;
  initiatedAt?: string;
  completedAt?: string;
  expiresAt?: string;
  reportAvailable?: boolean;
  profileReady?: boolean;
  profileValidationMessage?: string | null;
  hideStartButton?: boolean;
  requestSource?: "companys_contractor" | "cleaner_self";
  payment?: {
    packageTitle?: string;
    status?: string;
    amount?: number;
    currency?: string;
    currencySymbol?: string;
    date?: string;
  } | null;
};

export const BackgroundCheck = ({ contractorId, setLoader }: { contractorId: string; setLoader: (loading: boolean) => void }) => {
  const [backgroundCheck, setBackgroundCheck] =
    useState<BackgroundCheckDetails | null>(null);
  const [cards, setCards] = useState<PaymentCard[]>([]);
  const [packages, setPackages] = useState<CheckrPackage[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<CheckrPackage | null>(
    null,
  );
  const [selectedCard, setSelectedCard] = useState<PaymentCard | null>(null);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showCardDialog, setShowCardDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cardsLoaded, setCardsLoaded] = useState(false);
  const [detailsLoaded, setDetailsLoaded] = useState(false);

  const loadBackgroundCheck = async () => {
    const response = await getCompanyLatestBackgroundCheck(contractorId);
    setBackgroundCheck(response?.data || null);
  };

  useEffect(() => {
    let active = true;
    setLoader(true);
    const loadData = async () => {
      try {
        const [checkResponse, cardResponse] = await Promise.all([
          getCompanyLatestBackgroundCheck(contractorId),
          getCardDetails(),
        ]);
        if (!active) return;
        setBackgroundCheck(checkResponse?.data || null);
        setCards(cardResponse?.data?.data || []);
        setDetailsLoaded(true);
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            "Unable to load background check details",
        );
      } finally {
        if (active) setCardsLoaded(true);
        setLoader(false);
      }
    };
    loadData();
    return () => {
      active = false;
    };
  }, [contractorId]);

  const openPlanDialog = async () => {
    if (!cardsLoaded || !detailsLoaded) return;
    if (backgroundCheck?.profileReady !== true) {
      toast.error(
        backgroundCheck?.profileValidationMessage ||
          "Unable to validate the contractor profile. Please try again.",
      );
      return;
    }
    if (cards.length === 0) {
      toast.error(
        "Please add a card in Payment Settings before starting a background check.",
      );
      return;
    }
    if (
      backgroundCheck?.status &&
      !["INVITED", "EXPIRED"].includes(backgroundCheck.status || "")
    ) {
      toast.info(
        `A background check is already ${String(
          backgroundCheck.status || "in progress",
        ).toLowerCase()}.`,
      );
      return;
    }

    setLoading(true);
    try {
      const response = await getCheckrPackages();
      setPackages(response?.data || []);
      setShowPlanDialog(true);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Unable to load Checkr packages",
      );
    } finally {
      setLoading(false);
    }
  };

  const selectPackage = (checkrPackage: CheckrPackage) => {
    setSelectedPackage(checkrPackage);
    setSelectedCard(cards.find((card) => card.isDefault) || cards[0] || null);
    setShowPlanDialog(false);
    setShowCardDialog(true);
  };

  const confirmBackgroundCheck = async () => {
    if (!selectedPackage || !selectedCard) {
      toast.error("Select a package and payment card to continue.");
      return;
    }
    setLoading(true);
    try {
      const response = await startCompanyContractorBackgroundCheck(
        contractorId,
        {
          packageId: selectedPackage._id,
          paymentMethodId: selectedCard.id,
        },
      );
      if (!response?.success) {
        throw new Error(
          response?.message || "Failed to start the background check",
        );
      }
      toast.success("Background check invitation sent.");
      setShowCardDialog(false);
      await loadBackgroundCheck();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to start the background check",
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    setLoading(true);
    try {
      const response = await downloadCompanyBackgroundCheckReport(contractorId);
      const documents: Array<{
        index: number;
        type: string;
        filename: string;
      }> = response?.data || [];
      if (documents.length === 0) {
        toast.error("No PDF reports are available for this background check.");
        return;
      }

      for (const reportDocument of documents) {
        const file = await downloadCompanyBackgroundCheckReportDocument(
          contractorId,
          reportDocument.index,
        );
        const url = URL.createObjectURL(file);
        const link = document.createElement("a");
        link.href = url;
        link.download = reportDocument.filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          "Unable to download the background check report",
      );
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (checkrPackage: CheckrPackage) =>
    `${checkrPackage.currencySymbol || "$"}${Number(
      checkrPackage.price,
    ).toFixed(2)}`;
  const canStartBackgroundCheck =
    !backgroundCheck?.hideStartButton &&
    (!backgroundCheck?.status ||
      backgroundCheck.status.trim().toUpperCase() === "EXPIRED");
  const paymentStatusLabel = (status?: string) => {
    switch (status?.toLowerCase()) {
      case "hold":
      case "hold_created":
        return "On hold";
      case "captured":
        return "Paid";
      case "requires_reauth":
        return "Action required";
      case "hold_failed":
        return "Authorization failed";
      case "cancelled":
        return "Cancelled";
      default:
        return status
          ? status.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
          : "Unknown";
    }
  };
  const formatPaymentDate = (date?: string) => {
    if (!date) return null;
    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return null;

    const day = String(parsedDate.getDate()).padStart(2, "0");
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const year = parsedDate.getFullYear();
    const hours = parsedDate.getHours();
    const hour12 = hours % 12 || 12;
    const minutes = String(parsedDate.getMinutes()).padStart(2, "0");
    const period = hours >= 12 ? "PM" : "AM";

    return `${day}/${month}/${year} ${hour12}:${minutes} ${period}`;
  };

  return (
    <section className="rounded-lg bg-white p-6">
      <h2 className="mb-2 text-xl font-semibold">
        Enable Checkr Background Checks
      </h2>
      <p className="mb-5 text-sm text-gray-600">
        Invite this contractor to complete a Checkr background check. The
        selected card is authorized now and charged after the contractor starts
        the Checkr process.
      </p>

      {backgroundCheck?.status && (
        <div className="mb-5 rounded-md bg-gray-50 p-4">
          <p>
            <span className="font-semibold">Latest status:</span>{" "}
            {backgroundCheck.status || "Unknown"}
          </p>
          {backgroundCheck.completedAt && (
            <p className="mt-1 text-sm text-gray-600">
              Completed:{" "}
              {new Date(backgroundCheck.completedAt).toLocaleDateString()}
            </p>
          )}
          {backgroundCheck.requestSource === "companys_contractor" &&
            backgroundCheck.payment && (
              <div className="mt-3 border-t border-gray-200 pt-3 text-sm">
                {(backgroundCheck.payment.packageTitle ||
                  backgroundCheck.package) && (
                  <p>
                    <span className="font-semibold">Requested plan:</span>{" "}
                    {backgroundCheck.payment.packageTitle ||
                      backgroundCheck.package}
                  </p>
                )}
                <p className="mt-1">
                  <span className="font-semibold">Payment status:</span>{" "}
                  {paymentStatusLabel(backgroundCheck.payment.status)}
                  {typeof backgroundCheck.payment.amount === "number" &&
                    ` (${backgroundCheck.payment.currencySymbol || "$"}${backgroundCheck.payment.amount.toFixed(2)})`}
                </p>
                {backgroundCheck.payment.status?.toLowerCase() === "captured" &&
                  formatPaymentDate(backgroundCheck.payment.date) && (
                  <p className="mt-1 text-gray-600">
                    <span className="font-semibold text-gray-700">
                      Payment date and time:
                    </span>{" "}
                    {formatPaymentDate(backgroundCheck.payment.date)}
                  </p>
                )}
              </div>
            )}
          {backgroundCheck.reportAvailable && (
            <button
              type="button"
              onClick={downloadReport}
              disabled={loading}
              className="mt-3 rounded-md border border-[#0078ae] px-4 py-2 font-medium text-[#0078ae] disabled:opacity-50"
            >
              Download report
            </button>
          )}
        </div>
      )}

      {canStartBackgroundCheck && (
        <button
          type="button"
          onClick={openPlanDialog}
          disabled={!cardsLoaded || !detailsLoaded || loading}
          className="rounded-md bg-[#0078ae] px-5 py-3 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? "Please wait..." : "Start Background Check"}
        </button>
      )}

      {showPlanDialog && (
        <CheckrPackageSelectionDialog
          packages={packages}
          onClose={() => setShowPlanDialog(false)}
          onSelectPackage={selectPackage}
        />
      )}

      {showCardDialog && selectedPackage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-xl rounded-lg bg-white p-6">
            <h3 className="mb-4 text-xl font-semibold">Select a payment card</h3>
            <div className="mb-5 space-y-3">
              {cards.map((card) => (
                <label
                  key={card.id}
                  className="flex cursor-pointer items-center gap-3 rounded-md border border-gray-200 p-3"
                >
                  <input
                    type="radio"
                    name="checkr-payment-card"
                    checked={selectedCard?.id === card.id}
                    onChange={() => setSelectedCard(card)}
                  />
                  <span>
                    {card.card?.brand || "Card"} ending in{" "}
                    {card.card?.last4 || "----"}
                    {card.isDefault ? " (Default)" : ""}
                  </span>
                </label>
              ))}
            </div>
            <p className="mb-5 rounded-md bg-gray-50 p-4 text-sm">
              We’ll place a temporary authorization for{" "}
              {formatPrice(selectedPackage)} on the card ending in{" "}
              {selectedCard?.card?.last4 || "----"}. The payment will be
              captured once the contractor begins the background check.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCardDialog(false)}
                className="rounded-md border border-gray-300 px-4 py-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmBackgroundCheck}
                disabled={loading || !selectedCard}
                className="rounded-md bg-[#0078ae] px-4 py-2 font-semibold text-white disabled:opacity-50"
              >
                {loading ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
